import { useState } from 'react';
import { useQuery, useMutation } from '@tanstack/react-query';

import { queryClient } from '@/lib/queryClient';
import { authedJson, authedFetch } from '@/lib/http';
import { useToast } from '@/hooks/use-toast';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { formatDateCompact } from '@/lib/dateUtils';

interface PayoutJob {
  id: string;
  creatorId: string | null;
  creatorDisplayName: string | null;
  payeeType: string;
  payeeId: string | null;
  orderId: string;
  eligibleAt: string | null;
  status: string;
  stripeTransferId: string | null;
  holdReason: string | null;
  creatorEarningsCents: number | null;
  venueEarningsCents: number | null;
  ambassadorEarningsCents: number | null;
}

const STATUSES = ['scheduled', 'blocked', 'failed', 'completed', 'all'] as const;

const usd = (c: number) => `$${(c / 100).toFixed(2)}`;

const payeeLabel = (p: PayoutJob): string => {
  if (p.payeeType === 'creator') return p.creatorDisplayName || 'Creator';
  if (p.payeeType === 'venue_owner') return `Venue (${(p.payeeId ?? '').slice(0, 8)})`;
  if (p.payeeType === 'ambassador') return `Ambassador (${(p.payeeId ?? '').slice(0, 8)})`;
  return p.payeeType;
};

const amountFor = (p: PayoutJob): number =>
  p.payeeType === 'venue_owner'
    ? (p.venueEarningsCents ?? 0)
    : p.payeeType === 'ambassador'
      ? (p.ambassadorEarningsCents ?? 0)
      : (p.creatorEarningsCents ?? 0);

const statusVariant = (s: string): 'default' | 'secondary' | 'destructive' | 'outline' => {
  if (s === 'completed') return 'default';
  if (s === 'blocked' || s === 'failed') return 'destructive';
  return 'outline';
};

export function PayoutsAdmin() {
  const { toast } = useToast();
  const [status, setStatus] = useState<(typeof STATUSES)[number]>('scheduled');

  const { data: payoutsRaw, isLoading } = useQuery<unknown>({
    queryKey: ['/api/admin/financials/payouts', status],
    queryFn: () =>
      authedJson(`/api/admin/financials/payouts?${status === 'all' ? '' : `status=${status}`}`),
  });
  const payouts: PayoutJob[] = Array.isArray(payoutsRaw)
    ? (payoutsRaw as PayoutJob[])
    : ((payoutsRaw as { data?: PayoutJob[] } | null)?.data ?? []);

  const act = useMutation({
    mutationFn: async (v: { id: string; action: 'hold' | 'retry' | 'mark-failed'; reason: string }) => {
      const res = await authedFetch(`/api/admin/financials/payouts/${v.id}/${v.action}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ reason: v.reason }),
      });
      if (!res.ok) throw new Error((await res.json().catch(() => ({}))).error || 'Failed');
      return res.json();
    },
    onSuccess: () => {
      toast({ title: 'Payout updated' });
      queryClient.invalidateQueries({ queryKey: ['/api/admin/financials/payouts'] });
    },
    onError: (e: Error) =>
      toast({ title: 'Payout action failed', description: e.message, variant: 'destructive' }),
  });

  const run = (id: string, action: 'hold' | 'retry' | 'mark-failed') => {
    const reason = window.prompt(`Reason for "${action}" (min 10 characters):`)?.trim();
    if (!reason) return;
    if (reason.length < 10) {
      toast({ title: 'Reason too short', description: 'At least 10 characters.', variant: 'destructive' });
      return;
    }
    act.mutate({ id, action, reason });
  };

  return (
    <Card>
      <CardHeader className="flex-row items-center justify-between gap-3 space-y-0">
        <div>
          <CardTitle className="text-base">Payouts</CardTitle>
          <CardDescription>Scheduled transfers to creators, venues, and ambassadors.</CardDescription>
        </div>
        <select
          aria-label="Payout status filter"
          className="rounded-lg border border-border bg-background px-3 py-2 text-sm"
          value={status}
          onChange={e => setStatus(e.target.value as (typeof STATUSES)[number])}
        >
          {STATUSES.map(s => (
            <option key={s} value={s}>
              {s[0].toUpperCase() + s.slice(1)}
            </option>
          ))}
        </select>
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <p className="text-sm text-muted-foreground">Loading…</p>
        ) : payouts.length === 0 ? (
          <p className="text-sm text-muted-foreground">No {status === 'all' ? '' : status} payouts.</p>
        ) : (
          <div className="overflow-x-auto overscroll-x-contain">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b text-left text-xs uppercase tracking-wide text-muted-foreground">
                  <th className="py-2 pr-4 font-medium">Payee</th>
                  <th className="py-2 pr-4 text-right font-medium">Amount</th>
                  <th className="py-2 pr-4 font-medium">Eligible</th>
                  <th className="py-2 pr-4 font-medium">Status</th>
                  <th className="py-2 font-medium">
                    <span className="sr-only">Actions</span>
                  </th>
                </tr>
              </thead>
              <tbody>
                {payouts.map(p => (
                  <tr key={p.id} className="border-b last:border-0 align-top">
                    <td className="py-2 pr-4">
                      <div className="font-medium">{payeeLabel(p)}</div>
                      {p.holdReason && (
                        <div className="text-xs text-destructive">{p.holdReason}</div>
                      )}
                    </td>
                    <td className="py-2 pr-4 text-right tabular-nums">{usd(amountFor(p))}</td>
                    <td className="py-2 pr-4 text-muted-foreground">
                      {p.eligibleAt ? formatDateCompact(p.eligibleAt) : '—'}
                    </td>
                    <td className="py-2 pr-4">
                      <Badge variant={statusVariant(p.status)} className="capitalize">
                        {p.status}
                      </Badge>
                    </td>
                    <td className="py-2 text-right">
                      <div className="flex flex-wrap justify-end gap-1">
                        {(p.status === 'scheduled' || p.status === 'blocked') && (
                          <Button size="sm" variant="outline" onClick={() => run(p.id, 'hold')}>
                            Hold
                          </Button>
                        )}
                        {(p.status === 'blocked' || p.status === 'failed') && (
                          <Button size="sm" variant="outline" onClick={() => run(p.id, 'retry')}>
                            Retry
                          </Button>
                        )}
                        {(p.status === 'scheduled' || p.status === 'blocked') && (
                          <Button
                            size="sm"
                            variant="outline"
                            className="text-destructive"
                            onClick={() => run(p.id, 'mark-failed')}
                          >
                            Mark failed
                          </Button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
